import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import { usePermissions } from '../../auth';
import { TableCard, TableSkeleton, TableEmpty, TableError, Pager, FilterBar, TH, TD, ROW, EnrollmentBadge, RegisterFaceLink, RowButton, PersonCell, PhotoPips, usePagination, PageSizeSelect, RECORD_PAGE_SIZE, useSort, SortableTH, timeKey, FILTER_BTN, FILTER_BTN_PRIMARY, FilterCount, FilterActions } from './tableUi';
import StatusTag from '../../components/common/StatusTag';
import EmployeeAvatar from './EmployeeAvatar';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  FilterCheck, FilterColumn, FilterSection, FilterSearch, FILTER_CONTROL, PageWithFilters, useFilterColumn,
} from '../../components/layout/FilterColumn';

/**
 * Columns the employee list can be ordered by. The only time this table carries is when a
 * face was registered, which doubles as "who is still waiting to be enrolled" — the blanks
 * sort to the bottom, so descending puts the never-registered last and ascending surfaces
 * the longest-standing enrolments first.
 */
const EMPLOYEE_SORTS = {
  name: (e: any) => e.name || '',
  designation: (e: any) => e.designation || '',
  department: (e: any) => e.department || '',
  registered_at: (e: any) => timeKey(e.vector_registered_at),
};

/** Face ID filter choices; '' = any. */
const FACE_STATES = [
  { value: '', label: 'Any state' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'not_enrolled', label: 'Not enrolled' },
  { value: 'no_photos', label: 'No photos yet' },
] as const;

/** UC2 — employee directory: create, edit, 5-photo enrollment + preview, AI enroll status. */
export interface EmployeesTabProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
  onOpenEmployee: (id: string) => void;
}

export default function EmployeesTab({ nodeKey = '', onOpenEmployee }: EmployeesTabProps) {
  const { attendance } = useApi();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('attendance.create_employee');
  const canEdit = hasPermission('attendance.edit_employee');
  const canRegister = hasPermission('attendance.register_employee'); // backend also enforces
  const canDelete = hasPermission('attendance.delete_employee');       // backend also enforces
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  /**
   * Face-ID state filter. The directory's most common job is working the enrolment backlog —
   * "who still has no face registered" — which previously meant reading down the badge
   * column of every page.
   */
  const [faceState, setFaceState] = useState<'' | 'enrolled' | 'not_enrolled' | 'no_photos'>('');
  const filterCol = useFilterColumn('attendance-employees');
  const [showCreate, setShowCreate] = useState(false);
  const [editFor, setEditFor] = useState<any>(null);
  const [enrollFor, setEnrollFor] = useState<any>(null);
  const [deleteFor, setDeleteFor] = useState<any>(null);
  /**
   * Kept apart from the tab's `error`, which only renders in place of an empty
   * table — a failed delete on a populated list would set it and show nothing.
   * This one stays inside the dialog, next to the button that caused it.
   */
  const [deleteError, setDeleteError] = useState('');

  /**
   * Registered people who are NOT yet employees.
   *
   * The directory's other backlog. The enrolment backlog above is "which employees still have
   * no face"; this is its mirror — faces the estate already recognises that nobody has decided
   * about. Both end at the same place, an employee with a working Face ID, and an operator
   * working one is usually the right person to work the other.
   *
   * Collapsed by default and only fetched when opened: most visits to this tab are about
   * employees, and a second request per visit for a section nobody looked at is a request
   * nobody asked for.
   */
  const [showPeople, setShowPeople] = useState(false);
  const [people, setPeople] = useState<any[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState('');
  const [promoting, setPromoting] = useState<string | null>(null);

  const loadPeople = useCallback(async () => {
    setPeopleLoading(true);
    setPeopleError('');
    try {
      setPeople(await attendance.listRegisteredPeople({ is_employee: false, limit: 200 }));
    } catch (e: any) {
      setPeopleError(e?.message || 'Could not load the person registry');
    } finally { setPeopleLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, nodeKey]);

  useEffect(() => { if (showPeople) loadPeople(); }, [showPeople, loadPeople]);

  /**
   * Make a registered person an employee.
   *
   * No re-enrolment: the AI module keys vectors by person_id and this does not change it, so
   * the face that already matches keeps matching. What changes is that their sightings start
   * being counted as attendance — which is why the confirmation says so rather than leaving it
   * to be discovered at the end of the month.
   */
  const promote = async (person: any) => {
    setPromoting(person.person_id);
    try {
      const res: any = await attendance.markPersonAsEmployee(person.person_id, { name: person.name });
      // The node reports how many earlier sightings it is NOT counting. Silence here would
      // let an operator assume the person's history came with them.
      if (res?.prior_inout_sightings) {
        alert(`${person.name} is now an employee.\n\n${res.note}`);
      }
      await Promise.all([load(), loadPeople()]);
    } catch (e: any) {
      alert(`Could not make ${person.name} an employee: ${e?.response?.data?.message || e?.message}`);
    } finally { setPromoting(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await attendance.listEmployees({ limit: 500 });
      setRows(Array.isArray(r) ? r : []);
    } catch (e: any) {
      console.error(e);
      // Kept out of `rows` so a failed reload doesn't silently blank a list that loaded
      // fine a moment ago — the error replaces the table only when there is nothing to show.
      setError(e?.message || 'Request failed');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, nodeKey]);
  useEffect(() => { load(); }, [load]);

  /** Departments actually present, for the filter — sorted, de-duplicated, blanks dropped. */
  const departments = useMemo(
    () => Array.from(new Set(rows.map((e) => e.department).filter(Boolean))).sort() as string[],
    [rows],
  );

  // Name / code / department search, matching the Presence and Payroll filter behaviour.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((e) => {
      if (department && (e.department || '') !== department) return false;
      if (faceState === 'enrolled' && !e.vector_registered) return false;
      if (faceState === 'not_enrolled' && e.vector_registered) return false;
      // "No photos" is the subset of not-enrolled that somebody has to go and photograph,
      // as opposed to the ones already waiting on the AI module.
      if (faceState === 'no_photos' && (e.vector_registered || (e.photo_count || 0) > 0)) return false;
      if (!q) return true;
      return [e.name, e.employee_code, e.department, e.designation].some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [rows, search, department, faceState]);

  const { sorted, sort, toggleSort } = useSort(filtered, EMPLOYEE_SORTS);

  const { pageItems, pagerProps, pageSizeProps, setPage } = usePagination(sorted, RECORD_PAGE_SIZE);

  // After a filter or reorder, "page 3" refers to entirely different people.
  useEffect(() => { setPage(1); }, [search, department, faceState, sort, setPage]);

  const filtersActive = !!search || !!department || !!faceState;

  /**
   * On failure the dialog STAYS OPEN carrying the reason. Closing it would leave the
   * row sitting there with no indication the delete had failed, which reads as
   * "it worked but the list is stale" — and the obvious next move, pressing Delete
   * again, is the wrong instinct to encourage on a destructive action.
   */
  const confirmDelete = async () => {
    if (!deleteFor) return;
    setDeleteError('');
    try {
      await attendance.deleteEmployee(deleteFor.id);
      setDeleteFor(null);
      await load();
    } catch (e: any) {
      setDeleteError(e?.message || 'Delete failed');
    }
  };

  const clearFilters = () => { setSearch(''); setDepartment(''); setFaceState(''); };
  const activeCount = [!!search, !!department, !!faceState].filter(Boolean).length;

  return (
    <PageWithFilters
      filters={
        <FilterColumn
          open={filterCol.open}
          onToggle={filterCol.toggle}
          label="Employee filters"
          activeCount={activeCount}
          onClearAll={clearFilters}
        >
          <FilterSection title="Search">
            <FilterSearch value={search} onChange={setSearch} placeholder="Name, code or department…" />
          </FilterSection>

          {departments.length > 0 && (
            <FilterSection title="Department">
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                aria-label="Filter by department"
                className={FILTER_CONTROL}
              >
                <option value="">All departments</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FilterSection>
          )}

          <FilterSection title="Face ID">
            <div data-tour="attendance-employees-face" role="radiogroup" aria-label="Filter by Face ID state" className="space-y-0.5">
              {FACE_STATES.map((o) => (
                <FilterCheck key={o.value} radio checked={faceState === o.value} onClick={() => setFaceState(o.value)} label={o.label} />
              ))}
            </div>
          </FilterSection>
        </FilterColumn>
      }
    >
      <FilterBar>
        <FilterCount>
          {filtersActive ? `${filtered.length} of ${rows.length}` : `${rows.length}`} employees
        </FilterCount>

        {filtersActive && (
          <button onClick={clearFilters} className={FILTER_BTN}>
            <XMarkIcon className="h-3.5 w-3.5" /> Clear
          </button>
        )}

        <FilterActions>
          <PageSizeSelect {...pageSizeProps} />
          {canCreate && (
            <button data-tour="attendance-employees-add" onClick={() => setShowCreate(true)} className={FILTER_BTN_PRIMARY}>
              <PlusIcon className="h-4 w-4" /> Add employee
            </button>
          )}
        </FilterActions>
      </FilterBar>

      <TableCard>
        {loading ? (
          <TableSkeleton />
        ) : error && rows.length === 0 ? (
          <TableError message={error} onRetry={load}>Couldn&apos;t load employees</TableError>
        ) : rows.length === 0 ? (
          <TableEmpty>No employees yet.</TableEmpty>
        ) : filtered.length === 0 ? (
          <TableEmpty>No employees match these filters.</TableEmpty>
        ) : (
          <>
            {/* `table-fixed`: the widths come from the header rather than from whichever name
                on THIS page happens to be longest, so paging doesn't shuffle the columns
                sideways — and it's what makes the text cells truncate instead of stretching.

                Percentage shares summing to 100, not rem: with fixed widths every column but
                the first sits as narrow as its content, so all the slack on a wide screen
                lands in the name column and squeezes the enrolment state this page is for.
                The name gets 23% — enough for a full name beside its photo, and no longer
                the dumping ground it was when it also carried the designation.

                The floor is what the widest ROW needs, not the header: three action buttons
                (~14rem) and the 5-pip meter (~8rem) stop rendering cleanly below ~66rem, and
                the card scrolls horizontally rather than crushing them. */}
            <table className="w-full min-w-[66rem] table-fixed text-[13px]">
              <colgroup>
                <col className="w-[23%]" />
                <col className="w-[14%]" />
                <col className="w-[13%]" />
                <col className="w-[15%]" />
                <col className="w-[13%]" />
                <col className="w-[22%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <SortableTH label="Name" columnKey="name" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Designation" columnKey="designation" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Department" columnKey="department" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Face ID" columnKey="registered_at" sort={sort} onSort={toggleSort}
                    title="Face ID as confirmed by the AI module. Sorts by when the face was registered; employees with no registration sort last" />
                  <th className={TH} title="Enrollment photos on file, of the 5 the AI module works best with">Photos</th>
                  <th className={`${TH} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((e) => (
                  <tr key={e.id} className={ROW}>
                    <td className={TD}>
                      {/* Only the code on the sub-line now — the designation reads as its own
                          column, where it can be scanned down a roster and sorted, instead of
                          being buried in a "EMP-1042 · Line Inspector" string. */}
                      <PersonCell
                        name={e.name}
                        code={e.employee_code}
                        avatar={<EmployeeAvatar photoId={e.primary_photo_id} name={e.name} />}
                        onClick={() => onOpenEmployee(e.id)}
                        badge={e.status === 'inactive' ? <StatusTag tone="neutral" dot={false}>Inactive</StatusTag> : undefined}
                      />
                    </td>
                    <td className={`${TD} truncate`} title={e.designation || ''}>{e.designation || '—'}</td>
                    <td className={`${TD} truncate`} title={e.department || ''}>{e.department || '—'}</td>
                    <td className={TD}>
                      <EnrollmentBadge registered={e.vector_registered} at={e.vector_registered_at} photoCount={e.photo_count} />
                    </td>
                    <td className={TD}><PhotoPips count={e.photo_count} registered={e.vector_registered} /></td>
                    <td className={`${TD} whitespace-nowrap text-right`}>
                      <div className="inline-flex items-center gap-1.5">
                        {canEdit && <RowButton onClick={() => setEditFor(e)} title="Edit this employee's details">Edit</RowButton>}
                        {canRegister && <RegisterFaceLink onClick={() => setEnrollFor(e)} label="Photos" title="Manage enrollment photos & register face" />}
                        {canDelete && (
                          <RowButton
                            tone="danger"
                            onClick={() => { setDeleteError(''); setDeleteFor(e); }}
                            title="Delete this employee and their attendance record"
                          >
                            Delete
                          </RowButton>
                        )}
                        {!canEdit && !canRegister && !canDelete && <span className="text-xs text-muted-foreground">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager {...pagerProps} />
          </>
        )}
      </TableCard>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {editFor && <EditModal employee={editFor} onClose={() => setEditFor(null)} onSaved={() => { setEditFor(null); load(); }} />}
      {enrollFor && <EnrollModal employee={enrollFor} onClose={() => setEnrollFor(null)} onDone={() => { setEnrollFor(null); load(); }} />}

      {/* Spells out what goes with the person rather than asking "are you sure?".
          Deleting an employee cascades six tables — attendance_days,
          attendance_sessions, attendance_sightings, payroll_adjustments,
          employee_face_photos and employee_face_registration — then removes the
          `persons` registry row (cascading person_inout_log) and publishes a
          delete to the AI module so it drops the face vectors. An operator whose
          intent was "this person left the company" has no way to know from the
          button that they are also erasing the attendance history somebody may
          need for payroll, so the dialog says it. */}
      {/* ── Registered people who are not employees ──
          Placed under the directory rather than in a tab of its own: it is the same job seen
          from the other side, and an operator who has just noticed somebody missing from the
          list should find them here without navigating. */}
      {canCreate && (
        <section className="mt-4 rounded-xl border border-border bg-card">
          <button
            type="button"
            onClick={() => setShowPeople((v) => !v)}
            aria-expanded={showPeople}
            className="flex w-full items-center justify-between px-4 py-3 text-left"
          >
            <span>
              <span className="text-[14px] font-semibold text-foreground">
                Registered people who are not employees
              </span>
              <span className="mt-0.5 block text-[12px] text-muted-foreground">
                Faces the estate already recognises. Making one an employee starts counting
                their attendance — no re-enrolment, the same face keeps matching.
              </span>
            </span>
            <span className="ml-3 shrink-0 text-[12px] text-muted-foreground">
              {showPeople ? 'Hide' : 'Show'}
            </span>
          </button>

          {showPeople && (
            <div className="border-t border-border px-4 py-3">
              {peopleLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />)}
                </div>
              ) : peopleError ? (
                <p className="py-4 text-center text-[13px] text-destructive">{peopleError}</p>
              ) : people.length === 0 ? (
                <p className="py-4 text-center text-[13px] text-muted-foreground">
                  Everyone in the registry is already an employee. New people are registered
                  from a face event on the FRS Face Recognition report.
                </p>
              ) : (
                <ul className="space-y-2">
                  {people.map((person) => (
                    <li
                      key={person.person_id}
                      className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-foreground">
                          {person.name || person.person_id}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          {person.label && (
                            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase bg-gray-500/15 text-muted-foreground">
                              {person.label}
                            </span>
                          )}
                          {/* The enrolment state matters more here than in the directory:
                              promoting somebody whose vectors never landed produces an
                              employee the cameras cannot recognise. */}
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                            person.vector_registered
                              ? 'bg-emerald-500/10 text-emerald-500'
                              : 'bg-amber-500/10 text-amber-500'
                          }`}>
                            {person.vector_registered ? 'face enrolled' : 'enrolment pending'}
                          </span>
                          {person.is_blacklisted && (
                            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-red-500/15 text-red-500">
                              blacklisted
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => promote(person)}
                        disabled={promoting === person.person_id}
                        className="shrink-0 rounded-lg bg-brand px-3 py-1.5 text-[12px] font-medium text-white hover:bg-brand-dark disabled:opacity-50 transition-colors"
                      >
                        {promoting === person.person_id ? 'Working…' : 'Make employee'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      <ConfirmDialog
        open={!!deleteFor}
        title="Delete employee"
        confirmLabel="Delete employee"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteFor(null); setDeleteError(''); }}
        message={
          <>
            <span className="text-foreground font-medium">
              {deleteFor?.name}
              {deleteFor?.employee_code ? ` (${deleteFor.employee_code})` : ''}
            </span>{' '}
            and everything recorded against them will be removed:
            <ul className="mt-2 list-disc space-y-0.5 pl-5">
              <li>their attendance history — every day, session and sighting</li>
              <li>any payroll adjustments entered for them</li>
              <li>their enrollment photos and Face ID registration</li>
              <li>
                their identity in the person registry — including any sightings recorded
                before they became an employee
              </li>
            </ul>
            <span className="mt-2 block">
              Past attendance reports will no longer include this person. This cannot be undone.
            </span>
            {/* Marking them Inactive is what most operators actually want here, and it
                is one click away in Edit — worth saying before they commit, not after. */}
            <span className="mt-2 block text-xs">
              To keep the record and only stop tracking them, set their status to
              Inactive with <span className="text-foreground">Edit</span> instead.
            </span>
            {deleteError && (
              <span className="mt-3 block text-destructive">{deleteError}</span>
            )}
          </>
        }
      />
    </PageWithFilters>
  );
}

function Modal({ children, onClose }: { children: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl p-6 w-full max-w-lg max-h-[88vh] overflow-y-auto shadow-modal border border-border" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

const FIELDS = ['name', 'employee_code', 'department', 'designation', 'email', 'phone'] as const;

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { attendance } = useApi();
  const [form, setForm] = useState<any>({ name: '', employee_code: '', department: '', designation: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try { await attendance.createEmployee(form); onCreated(); } catch (e: any) { setErr(e.message || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-semibold text-foreground mb-4">Add employee</h3>
      <div className="space-y-3">
        {FIELDS.map((k) => (
          <label key={k} className="block">
            <span className="text-xs text-muted-foreground capitalize">{k.replace('_', ' ')}{k === 'name' ? ' *' : ''}</span>
            <input value={form[k] || ''} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground" />
          </label>
        ))}
        {err && <div className="text-sm text-destructive">{err}</div>}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="px-3 py-2 rounded-lg bg-brand text-white hover:bg-brand-dark text-sm disabled:opacity-50">{saving ? 'Saving…' : 'Create'}</button>
      </div>
    </Modal>
  );
}

function EditModal({ employee, onClose, onSaved }: { employee: any; onClose: () => void; onSaved: () => void }) {
  const { attendance } = useApi();
  const [form, setForm] = useState<any>({
    name: employee.name || '', employee_code: employee.employee_code || '', department: employee.department || '',
    designation: employee.designation || '', email: employee.email || '', phone: employee.phone || '',
    status: employee.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try { await attendance.updateEmployee(employee.id, form); onSaved(); } catch (e: any) { setErr(e.message || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-semibold text-foreground mb-4">Edit employee</h3>
      <div className="space-y-3">
        {FIELDS.map((k) => (
          <label key={k} className="block">
            <span className="text-xs text-muted-foreground capitalize">{k.replace('_', ' ')}{k === 'name' ? ' *' : ''}</span>
            <input value={form[k] || ''} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground" />
          </label>
        ))}
        <label className="block">
          <span className="text-xs text-muted-foreground">Status</span>
          <select value={form.status} onChange={(e) => set('status', e.target.value)} className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground">
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </label>
        {err && <div className="text-sm text-destructive">{err}</div>}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="px-3 py-2 rounded-lg bg-brand text-white hover:bg-brand-dark text-sm disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

/** Thumbnail that fetches an enrollment photo through the proxy (binary → data URL). */
function PhotoThumb({ photo, onRemove }: { photo: any; onRemove?: (photo: any) => void }) {
  const { attendance } = useApi();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    attendance.employeePhotoDataUrl(photo.id).then((u) => { if (alive) setSrc(u); });
    return () => { alive = false; };
  }, [attendance, photo.id]);
  return (
    <div className="relative group">
      {src
        ? <img src={src} alt="" className="h-16 w-16 object-cover rounded border border-border" />
        : <div className="h-16 w-16 rounded border border-border bg-muted flex items-center justify-center text-[10px] text-muted-foreground">…</div>}
      {photo.source === 'unknown_event' && <span className="absolute -top-1 -left-1 bg-brand text-white text-[9px] px-1 rounded" title="Linked from an unknown face event">link</span>}
      {onRemove && (
        <button
          onClick={() => onRemove(photo)}
          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-white text-[11px] leading-none flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
          title={photo.source === 'unknown_event' ? 'Unlink this face' : 'Remove this photo'}
        >
          ✕
        </button>
      )}
    </div>
  );
}

function EnrollModal({ employee, onClose, onDone }: { employee: any; onClose: () => void; onDone: () => void }) {
  const { attendance } = useApi();
  const { hasPermission } = usePermissions();
  const canRemovePhoto = hasPermission('attendance.edit_employee'); // backend also enforces
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [detail, setDetail] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadDetail = useCallback(async () => {
    try { setDetail(await attendance.getEmployee(employee.id)); } catch { /* ignore */ }
  }, [attendance, employee.id]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const existing = detail?.photos || [];
  const registered = detail?.vector_registered ?? employee.vector_registered;

  const removePhoto = async (photo: any) => {
    const verb = photo.source === 'unknown_event' ? 'Unlink this face from' : 'Remove this photo from';
    if (!confirm(`${verb} ${employee.name}? The AI module will drop the matching face vector.`)) return;
    try { await attendance.removeEmployeePhoto(employee.id, photo.id); await loadDetail(); onDone(); }
    catch (e: any) { setErr(e.message || 'Remove failed'); }
  };

  const pick = (list: FileList | null) => { if (list) setFiles(Array.from(list).slice(0, 5)); };
  const submit = async () => {
    if (!files.length) { setErr('Select 1–5 photos'); return; }
    setBusy(true); setErr('');
    try { await attendance.uploadEmployeePhotos(employee.id, files); setFiles([]); await loadDetail(); onDone(); }
    catch (e: any) { setErr(e.message || 'Upload failed'); } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-lg font-semibold text-foreground">Enrollment — {employee.name}</h3>
        {/* StatusTag rather than `bg-success/15`: Tailwind v3 drops the opacity modifier on a
            colour defined as a bare `var()`, so this pill used to render with no fill. */}
        <span className="ml-auto">
          <StatusTag tone={registered ? 'success' : 'warning'}>
            {registered ? 'Face ID enrolled' : 'Not enrolled'}
          </StatusTag>
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Photos are stored and sent to the trinetra-ai-analytics-module to register this person's face vector.</p>

      {existing.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-muted-foreground mb-2">{existing.length} enrolled photo(s){canRemovePhoto && existing.length > 0 ? ' — hover to remove' : ''}</div>
          <div className="flex gap-2 flex-wrap">
            {existing.map((p: any) => <PhotoThumb key={p.id} photo={p} onRemove={canRemovePhoto ? removePhoto : undefined} />)}
          </div>
        </div>
      )}

      <div onClick={() => inputRef.current?.click()} className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-muted/40">
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => pick(e.target.files)} />
        <div className="text-sm text-foreground">{files.length ? `${files.length} photo(s) selected` : 'Click to add photos'}</div>
        <div className="text-xs text-muted-foreground mt-1">JPG/PNG, up to 5 — added as more angles</div>
      </div>
      {files.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {files.map((f, i) => <img key={i} src={URL.createObjectURL(f)} alt="" className="h-16 w-16 object-cover rounded border border-border" />)}
        </div>
      )}
      {err && <div className="text-sm text-destructive mt-3">{err}</div>}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm">Close</button>
        <button onClick={submit} disabled={busy || !files.length} className="px-3 py-2 rounded-lg bg-brand text-white hover:bg-brand-dark text-sm disabled:opacity-50">{busy ? 'Uploading…' : 'Upload & enroll'}</button>
      </div>
    </Modal>
  );
}
